import useTranslation from 'next-translate/useTranslation';

// Third-party
import clsx from 'clsx';
import { includes } from 'lodash';

// Hooks
import { useMessageFilter } from '@/utils/hooks/conversations/useMessageFilter';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import type { SocialInboxType } from '@/components/pages/conversations/SocialInbox/types';

import { INSTAGRAM } from '@/lib/socialProfile';
// Types/Libs
import {
    DEFAULT_SOCIAL_INBOX_TYPES,
    INSTAGRAM_ONLY_FEATURES,
    MESSAGE_FILTER_META,
    SOCIAL_INBOX_MANAGING_OPTIONS
} from '@/components/pages/conversations/SocialInbox/const';

// Components
import Badge from '@/components/base/Badge';
import Tooltip from '@/components/base/Tooltip';
import Shining from '@/components/effects/Shining';
import { SocialInboxTypeTitle } from '@/components/pages/conversations/SocialInbox';
interface SocialInboxMenuDesktopProps {
    className?: string;
}

export const SocialInboxMenuDesktop = ({ className }: SocialInboxMenuDesktopProps) => {
    const { t } = useTranslation('conversations');

    const { activeSocialProfile } = useActiveSocialProfile();
    const { messageFilter, navigateToFilter } = useMessageFilter();

    const renderMenuItem = (socialInboxType: SocialInboxType) => {
        const isDirectMessage = socialInboxType === MESSAGE_FILTER_META.dm.key;
        const isInstagram = activeSocialProfile?.social_network_type === INSTAGRAM;
        const isInstagramOnlyFeature = includes(INSTAGRAM_ONLY_FEATURES, socialInboxType);
        const disabled = (isInstagramOnlyFeature && !isInstagram) || isDirectMessage;

        return (
            <Tooltip
                content={t('social_inbox.not_yet_available')}
                interactive={true}
                appendTo={() => document.body}
                placement="right"
                disabled={!disabled}>
                <span className="z-20 flex items-center">
                    <button
                        key={socialInboxType}
                        onClick={() => navigateToFilter(socialInboxType)}
                        disabled={disabled}
                        className={clsx(
                            'flex w-full items-center gap-2 space-x-2 overflow-hidden rounded-md p-2',
                            {
                                'bg-icoBlue-100 text-icoBlue dark:bg-icoBlue/20 dark:text-icoBlue':
                                    messageFilter === socialInboxType,
                                'text-icoGray-500 hover:bg-icoBlue-100 hover:text-icoBlue dark:text-icoDarkMode-wolf dark:hover:bg-icoBlue/20 dark:hover:text-icoBlue':
                                    messageFilter !== socialInboxType,
                                'cursor-not-allowed opacity-50': disabled
                            }
                        )}>
                        <SocialInboxTypeTitle
                            messageFilter={socialInboxType}
                            selected={messageFilter === socialInboxType}
                        />

                        {isDirectMessage && (
                            <Tooltip
                                content={t('social_inbox.coming_soon_dm')}
                                interactive={true}
                                appendTo={() => document.body}
                                placement="right">
                                <span className="z-22 flex items-center">
                                    <Shining className="rounded-full">
                                        <Badge variant="coming-soon" shorten />
                                    </Shining>
                                </span>
                            </Tooltip>
                        )}
                    </button>
                </span>
            </Tooltip>
        );
    };

    const renderMenuContainer = (title: string, items: readonly SocialInboxType[]) => {
        return (
            <div className="flex flex-col space-y-2">
                <span className="font-GSemiBold text-xs text-icoBlue dark:text-icoDarkMode-blueStats">
                    {title}
                </span>
                {items.map(renderMenuItem)}
            </div>
        );
    };

    return (
        <div
            className={`flex h-full flex-col space-y-8 bg-white dark:bg-icoDarkMode-deepBlack ${className}`}>
            {/* TODO: Uncomment when Direct Messages are available */}
            {/* <SocialInboxTitle /> */}

            {renderMenuContainer(t('social_inbox.inboxes'), DEFAULT_SOCIAL_INBOX_TYPES)}

            {renderMenuContainer(t('social_inbox.managing'), SOCIAL_INBOX_MANAGING_OPTIONS)}
        </div>
    );
};
